import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tag, Plus } from "lucide-react";
import { toast } from "sonner";

const AGENCIES = ["GS1", "HIBCC", "ICCBBA"];
const STATUSES = ["draft", "active", "obsolete"];
const PACKAGE_LEVELS = ["primary", "secondary", "tertiary", "unit_of_use"];

export default function UDIRegistry() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    udi_di: "", udi_pi: "", issuing_agency: "GS1",
    device_class: "", package_level: "primary", quantity_per_package: 1,
    effective_date: "", status: "draft",
  });

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data); if (data[0]) setPid(data[0].project_id);
    }).catch(() => {});
  }, [authLoading]);

  const load = useCallback(() => {
    if (!pid) return;
    api.get(`/udi/records?project_id=${pid}`).then(({ data }) => setRecords(data)).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = {
        ...form, project_id: pid,
        quantity_per_package: parseInt(form.quantity_per_package) || 1,
        udi_pi: form.udi_pi || null,
        effective_date: form.effective_date || null,
      };
      await api.post("/udi/records", payload);
      toast.success("UDI record created.");
      setOpen(false);
      setForm({ udi_di: "", udi_pi: "", issuing_agency: "GS1", device_class: "",
                package_level: "primary", quantity_per_package: 1,
                effective_date: "", status: "draft" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const updateStatus = async (udi_id, status) => {
    try {
      await api.patch(`/udi/records/${udi_id}`, { status });
      toast.success("Updated."); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="vq-overline mb-2 flex items-center gap-2"><Tag className="w-3 h-3" /> UDI Registry</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
              UDI-DI / UDI-PI Records
            </h1>
            <p className="text-slate-600 mt-2 max-w-3xl">
              GS1 / HIBCC / ICCBBA issuing-agency records for FDA GUDID + EUDAMED registration. UDI-DI is unique;
              re-use is refused.
            </p>
          </div>
          <Button data-testid="new-udi-btn" onClick={() => setOpen(true)} disabled={!pid}><Plus className="w-4 h-4 mr-1" /> New UDI</Button>
        </div>

        <div className="mb-4 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="udi-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.device_name || p.project_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="vq-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">UDI-DI</th>
                <th className="text-left px-3 py-2">Agency</th>
                <th className="text-left px-3 py-2">Package</th>
                <th className="text-left px-3 py-2">Effective</th>
                <th className="text-left px-3 py-2 w-48">Status</th>
              </tr>
            </thead>
            <tbody data-testid="udi-tbody">
              {records.length === 0 && <tr><td colSpan="5" className="text-center px-3 py-6 text-slate-400">No UDI records yet.</td></tr>}
              {records.map((r) => (
                <tr key={r.udi_id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">{r.udi_di}</div>
                    {r.udi_pi && <div className="text-xs text-slate-500 font-mono">PI: {r.udi_pi}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs"><Badge variant="secondary">{r.issuing_agency}</Badge></td>
                  <td className="px-3 py-2 text-xs">{r.package_level} × {r.quantity_per_package || 1}</td>
                  <td className="px-3 py-2 text-xs">{r.effective_date || "—"}</td>
                  <td className="px-3 py-2">
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.udi_id, v)}>
                      <SelectTrigger data-testid={`udi-status-${r.udi_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New UDI record</DialogTitle>
              <DialogDescription>UDI-DI must be unique. UDI-PI (production identifier) is optional and dynamic.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>UDI-DI (Device Identifier)</Label>
                <Input data-testid="udi-di" value={form.udi_di} onChange={(e) => setForm({ ...form, udi_di: e.target.value })} placeholder="01000012345678" />
              </div>
              <div className="col-span-2">
                <Label>UDI-PI (Production Identifier, optional)</Label>
                <Input data-testid="udi-pi" value={form.udi_pi} onChange={(e) => setForm({ ...form, udi_pi: e.target.value })} placeholder="(17)261231(10)LOT-A" />
              </div>
              <div>
                <Label>Issuing agency</Label>
                <Select value={form.issuing_agency} onValueChange={(v) => setForm({ ...form, issuing_agency: v })}>
                  <SelectTrigger data-testid="udi-agency"><SelectValue /></SelectTrigger>
                  <SelectContent>{AGENCIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Device class (optional)</Label>
                <Input data-testid="udi-class" value={form.device_class} onChange={(e) => setForm({ ...form, device_class: e.target.value })} placeholder="IIa / III / B / …" />
              </div>
              <div>
                <Label>Package level</Label>
                <Select value={form.package_level} onValueChange={(v) => setForm({ ...form, package_level: v })}>
                  <SelectTrigger data-testid="udi-pkg"><SelectValue /></SelectTrigger>
                  <SelectContent>{PACKAGE_LEVELS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Qty per package</Label>
                <Input data-testid="udi-qty" type="number" min={1} value={form.quantity_per_package} onChange={(e) => setForm({ ...form, quantity_per_package: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Effective date (optional)</Label>
                <Input data-testid="udi-date" type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="udi-submit" onClick={submit} disabled={form.udi_di.length < 8}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
